import Vue from 'vue'
import Router from 'vue-router'

import * as Flags from '@/libraries/util/flags'
import { store } from '@/store/store'
import { lazyLoad, preload } from '@/lazy'
import { Operation, Service } from '@/libraries/permissions/'

Vue.use(Router)

export const router = new Router({
  mode: 'history',
  name: 'router',
  routes: [
    {
      path: '/',
      name: 'Landing Page',
      component: lazyLoad('pages/Landing'),
    },
    {
      path: '/dashboard',
      name: 'Dashboard',
      component: preload('pages/Dashboard'),
      meta: {
        auth: true,
        navbar: false,
      },
    },
    {
      path: '/alerts',
      name: 'Alerts',
      component: preload('alerts/Alerts'),
      meta: {
        auth: true,
        navbar: true,
        dash: true,
        icon: 'exclamation-triangle',
        service: Service.ALERTING,
      },
    },
    {
      path: '/camera/all',
      name: 'Cameras',
      component: preload('cameras/CameraList'),
      meta: {
        auth: true,
        navbar: Flags.CamerasPage.enabled,
        dash: Flags.CamerasPage.enabled,
        icon: 'video',
        service: Service.VIDEO,
      },
    },
        {
      path: '/alert/snooze',
      name: 'Snooze Alert',
      component: lazyLoad('pages/SnoozeAlert'),
      props: route => ({
        alertConfigId: route.query.acid,
        cameraUUID: route.query.cuid,
        notificationTargetId: route.query.ntid,
        duration: route.query.dur ? Number(route.query.dur) : 3600, // in seconds
        singleUseToken: route.query.sut,
        snoozedAt: new Date().getTime() / 1000, // in seconds
      }),
      meta: {
        auth: true,
        service: Service.ALERTING,
        operation: Operation.SNOOZE,
      },
    },
    {
      path: '/camera/add',
      name: 'Add Camera',
      component: preload('pages/AddCamera'),
      meta: {
        auth: true,
        service: Service.VIDEO,
        operation: Operation.ADD_CAMERA,
      },
    },
    {
      path: '/view/:cameraId?/:timestamp?',
      name: 'Viewer',
      component: lazyLoad('viewer/Viewer'),
      props: true,
      meta: {
        auth: true,
        navbar: true,
        dash: true,
        icon: 'tv',
        service: Service.VIDEO,
      },
    },
    {
      path: '/faces/:id?',
      name: 'Faces',
      components: {
        master: preload('faces/FacesList'),
        detail: preload('faces/FacesDetails'),
      },
      meta: {
        auth: true,
        navbar: true,
        dash: true,
        icon: 'address-book',
        service: Service.FACES,
      },
    },
    {
      path: '/credential/:id?',
      name: 'Credential',
      components: {
        master: preload('credential-mismatch-report/CredentialMismatchIdentityList'),
        detail: preload('credential-mismatch-report/CredentialMismatchPanel'),
      },
      meta: {
        auth: true,
        navbar: true,
        dash: true,
        icon: 'id-badge',
        service: Service.KEYCARD,
      },
    },
    {
      path: '/staff/onboard',
      name: 'Add Domain',
      component: lazyLoad('onboarding/BuildingSetup'),
      meta: {
        auth: true,
        staff: true,
        service: Service.STAFF,
      },
    },
    {
      path: '/staff/onboard/addCameras',
      name: 'Add Cameras',
      component: lazyLoad('onboarding/AddCameras'),
      meta: {
        auth: true,
      },
    },
    {
      path: '/staff/train',
      name: 'Manage Faces System',
      component: lazyLoad('faces-system/Model'),
      meta: {
        auth: true,
        dash: false,
        nav: false,
        service: Service.FACES,
        operation: Operation.MANAGE_MODEL,
        staff: true,
      },
    },
    {
      path: '/staff/env',
      name: 'Environment',
      component: preload('staff/Env'),
      meta: {
        auth: true,
        staff: true,
        service: Service.STAFF,
        operation: Operation.VIEW_ENV_INFO,
      },
    },
    {
      path: '/staff/faces-dashboard',
      name: 'Faces Stats Dashboard',
      component: preload('pages/FacesStats'),
      meta: {
        auth: true,
        staff: true,
        service: Service.FACES,
        operation: Operation.VIEW_ALL,
      },
    },
    {
      // NB: a distinct path (not under /faces/...) so it doesn't collide with the '/faces/:id?' route.
      path: '/faces-settings',
      name: 'Faces Settings',
      component: preload('faces-settings/FacesSettings'),
      meta: {
        auth: true,
        staff: true,
        dash: false,
        navbar: false,
        service: Service.FACES,
        operation: Operation.CONFIGURE,
      },
    },
    {
      path: '/alert/settings',
      name: 'Alert Settings',
      component: preload('dna-configuration/ConfigurationPanel'),
      meta: {
        staff: true,
        auth: true,
        dash: false,
        navbar: false,
        service: Service.ALERTING,
        operation: Operation.EDIT,
      },
    },
    {
      path: '/alert/simplified-settings',
      name: 'Alert Settings',
      component: preload('dna-configuration/ConfigurationPanel'),
      meta: {
        staff: true,
        auth: true,
        dash: false,
        navbar: true,
        service: Service.ALERTING,
        operation: Operation.LIMITED_EDIT,
      },
    },
    {
      path: '/detected/:faceId?',
      name: 'Faces Training Tool',
      component: preload('faces/TrainingTool'),
      props: true,
      meta: {
        auth: true,
        dash: false,
        staff: true,
        service: Service.FACES,
        operation: Operation.TRAIN_MODEL,
      },
    },
    {
      path: '/detections',
      name: 'Detections',
      component: preload('faces/DetectionsTool'),
      props: true,
      meta: {
        auth: true,
        dash: false,
        staff: true,
        service: Service.FACES,
        operation: Operation.TRAIN_MODEL,
      },
    },
    {
      path: '/tracks/training',
      name: 'Tracks Training Tool',
      component: preload('faces/TracksTrainingTool'),
      props: true,
      meta: {
        auth: true,
        dash: false,
        staff: true,
        service: Service.FACES,
        operation: Operation.TRAIN_MODEL, /* TODO(yeedle): create new entitlement */
      },
    },
    {
      path: '/confirmed/:faceId?',
      name: 'Confirmed Faces',
      component: preload('shared/ConfirmedTags'),
      props: true,
      meta: {
        auth: true,
        dash: false,
        staff: true,
        service: Service.FACES,
        operation: Operation.TRAIN_MODEL,
      },
    },
    {
      path: '/staff/features',
      name: 'Feature Flags',
      component: lazyLoad('staff/FeatureFlags'),
      meta: {
        auth: true,
        dash: false,
        nav: false,
        staff: true,
        service: Service.STAFF,
        operation: Operation.FEATURE_FLAGS,
      },
    },
    {
      path: '/staff/storage',
      name: 'Diagnostic Export',
      component: lazyLoad('pages/Storage'),
      meta: {
        auth: true,
        dash: false,
        navbar: false,
        staff: true,
        service: Service.STAFF,
        operation: Operation.DIAGNOSTIC_EXPORT, /* TODO(yeedle): create new entitlement */
      },
    },
    {
      path: '/staff/mask_reports',
      name: 'Mask Reports Dashboard',
      component: lazyLoad('staff/MaskReport'),
      meta: {
        auth: true,
        dash: false,
        navbar: false,
        staff: true,
        service: Service.STAFF,
        operation: Operation.DIAGNOSTIC_EXPORT,
      },
    },
    {
      path: '/account/settings/pin',
      name: 'Set Pin',
      component: preload('user/UserPin'),
      meta: {
        staff: true,
        auth: true,
        dash: false,
        navbar: false,
        service: Service.ALERTING,
        operation: Operation.SET_DISMISS_PIN,
      },
    },
    {
      path: '/report/:id',
      name: 'Reports',
      component: preload('credential-mismatch-report/credential-report-tool/ReportContent'),
      meta: {
        staff: false,
        auth: true,
        dash: true,
        navbar: true,
        service: Service.KEYCARD,
        operation: Operation.VIEW_REPORTS,
        icon: 'file-alt',
      },
    },
    {
      path: '/realtime/credential',
      name: 'Realtime',
      component: preload('credential-mismatch-report/real-time-view/RealtimeContainer'),
      meta: {
        staff: false,
        auth: true,
        dash: true,
        navbar: true,
        service: Service.KEYCARD,
        operation: Operation.VIEW_REALTIME,
        domainPreference: 'has_realtime_credentials',
        icon: 'clipboard',
      },
    },
    {
      path: '/settings/camera-status',
      name: 'Camera Status',
      component: lazyLoad('pages/CameraStatus'),
      meta: {
        auth: true,
        staff: true,
        service: Service.VIDEO,
      },
    },
    {
      path: '/staff/ocl',
      name: 'OCL Config',
      component: lazyLoad('pages/OclDashboard'),
      meta: {
        auth: true,
        staff: true,
        service: Service.STAFF,
      },
    },
    {
      path: '/elopement-history',
      name: 'Elopement Alert History',
      component: lazyLoad('alerts/ElopementAlertHistory'),
      meta: {
        auth: true,
        staff: true,
        navbar: false,
        service: Service.ALERTING,
      },
    },
    {
      path: '/faces-alerts',
      name: 'View Faces Alerts',
      component: lazyLoad('alerts/FacesAlertsView'),
      meta: {
        auth: true,
        staff: true,
        navbar: false,
        dash: false,
        service: Service.ALERTING,
      },
    },
  ],
})

router.beforeEach((to, from, next) => {
  const isAuthenticated = store.getters.isAuthenticated
  const routeRequiresAuthentication = to.matched.some(
    record => record.meta.auth,
  )
  const isLandingPage = to.name === 'Landing Page'
  const isOAuthRedirect = to.hash.startsWith('#id_token=')
  const canAccessService = store.getters['entitlements/canAccessService']

  if (to.name === 'Loading Page') {
    return next()
  }
  if (isLandingPage) {
    if (isOAuthRedirect) {
      const hashParts = to.hash.replace('#id_token=', '').split('&')
      const token = hashParts[0]
      store.dispatch('validateJWT', { token }).catch(error => {
        next({
          path: '/',
          query: {
            message: error,
          },
        })
      })
    } else if (isAuthenticated) {
      next({
        path: '/dashboard',
      })
    } else {
      next()
    }
  } else if (routeRequiresAuthentication && !isAuthenticated) {
    next({
      path: '/',
      query: {
        redirect: to.fullPath,
        message: 'Please log in.',
      },
    })
  } else if (to.meta.service) {
    if (to.meta.service === 'account' && isAuthenticated) {
      next()
    }
    if (canAccessService(to.meta.service)) {
      store.commit('entitlements/setCurrentService', to.meta.service)
      next()
    } else {
      next({
        path: '/dashboard',
        query: {
          redirect: to.fullPath,
        },
      })
    }
  } else {
    next()
  }
})
